import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsEye, BsEyeSlash, BsGripVertical, BsLayoutThreeColumns, BsPencil, BsPlusLg } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';

/**
 * "Spalten" popover of a table: show/hide columns and reorder them by drag and drop. The layout
 * is saved per table (see useTableLayout), so every change applies immediately.
 */
const ColumnLayoutPicker = ({ items, isCustomized, toggle, move, reset, onAddColumn, addColumnLabel }) => {
	const [open, setOpen] = useState(false);
	const [dragKey, setDragKey] = useState(null);
	const hasHidden = items.some((item) => !item.visible);
	const closeAnd = (action) => () => {
		setOpen(false);
		action();
	};

	const trigger = (
		<Button onClick={() => setOpen((prev) => !prev)} variant={hasHidden ? 'solid' : 'outline'} size="sm">
			<BsLayoutThreeColumns className="w-3 h-3" /> Spalten
		</Button>
	);

	const resetAction = isCustomized && (
		<button type="button" onClick={reset} className="text-xs text-bgBase-800 hover:text-text">
			Zurücksetzen
		</button>
	);

	return (
		<Popover open={open} onClose={() => setOpen(false)} trigger={trigger} align="end">
			<Panel.Header actions={resetAction}>Spalten</Panel.Header>
			<Panel.Body padded={false} scroll>
				<div className="py-1">
					{items.map((item) => (
						<div
							key={item.key}
							draggable
							onDragStart={() => setDragKey(item.key)}
							onDragOver={(e) => {
								e.preventDefault();
								if (dragKey && dragKey !== item.key) move(dragKey, item.key);
							}}
							onDragEnd={() => setDragKey(null)}
							className={`flex items-center gap-2.5 px-3.5 py-2 cursor-grab active:cursor-grabbing transition-colors ${
								dragKey === item.key
									? 'bg-primary-100 border-y border-primary-200'
									: 'hover:bg-bgBase-300'
							}`}
						>
							<BsGripVertical className="w-3 h-3 text-bgBase-800 flex-shrink-0" />
							<span
								className={`flex-1 text-sm truncate select-none ${item.visible ? 'text-text' : 'text-bgBase-800'}`}
							>
								{item.label}
							</span>
							{item.onEdit && (
								<button
									type="button"
									onClick={closeAnd(item.onEdit)}
									title="Bearbeiten & testen"
									className="w-6 h-6 rounded-control flex items-center justify-center text-bgBase-800 hover:text-primary-600 hover:bg-primary-100 transition-colors"
								>
									<BsPencil className="w-3 h-3" />
								</button>
							)}
							<button
								type="button"
								onClick={() => toggle(item.key)}
								title={item.visible ? 'Ausblenden' : 'Einblenden'}
								className={`w-6 h-6 rounded-control flex items-center justify-center transition-colors ${
									item.visible
										? 'text-primary-600 hover:bg-primary-100'
										: 'text-bgBase-800 hover:bg-bgBase-300'
								}`}
							>
								{item.visible ? (
									<BsEye className="w-3.5 h-3.5" />
								) : (
									<BsEyeSlash className="w-3.5 h-3.5" />
								)}
							</button>
						</div>
					))}
				</div>
			</Panel.Body>
			{onAddColumn && (
				<Panel.Footer>
					<Button onClick={closeAnd(onAddColumn)} size="sm" fullWidth>
						<BsPlusLg className="w-3 h-3" /> {addColumnLabel}
					</Button>
				</Panel.Footer>
			)}
		</Popover>
	);
};

ColumnLayoutPicker.propTypes = {
	items: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.string,
			visible: PropTypes.bool,
			onEdit: PropTypes.func,
		}),
	).isRequired,
	isCustomized: PropTypes.bool.isRequired,
	toggle: PropTypes.func.isRequired,
	move: PropTypes.func.isRequired,
	reset: PropTypes.func.isRequired,
	onAddColumn: PropTypes.func,
	addColumnLabel: PropTypes.string,
};

export default ColumnLayoutPicker;
